import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

import { perspectiveFitDistance } from "./camera.ts";
import type { Footprint, RoofvizPacket, XY } from "./contract.ts";
import { roofVizDiagnostics } from "./diagnostics.ts";
import type { FasciaSpec } from "./fascia.ts";
import { buildFinishTopology, flashingDescriptor, type FinishTopologySegment } from "./finishTopology.ts";
import { gutterCrossSection, type GutterSpec } from "./gutters.ts";
import type { Colour, Profile } from "./materials.ts";
import {
  alignedModuleCentres,
  alignedSheetJoints,
  metalFinishForProfile,
  profileLodFromProjectedPixels,
  profileCrossSection,
  projectedModulePixels,
  type MetalFinishSpec,
  type ProfileLod,
} from "./metalFinish.ts";
import { objectPresentation } from "./objectPresentation.ts";
import type { RoofInsightId } from "./roofInsights.ts";
import type { SolveResult, SolvedLine, SolvedPlane } from "./solver.ts";

const D2R = Math.PI / 180;
const EPSILON = 1e-7;
const STAGE_BACKGROUND = 0xf1f4ef;

export type CardinalView = "north" | "east" | "south" | "west";

export interface SceneOptions {
  canvas: HTMLCanvasElement;
  container: HTMLElement;
  packet: RoofvizPacket;
  solved: SolveResult;
  footprints?: Footprint[];
  profile: Profile;
  colour: Colour;
  gutter: GutterSpec;
  fascia: FasciaSpec;
  northOffsetDeg?: number;
  autoRotate?: boolean;
  autoRotateSpeed?: number;
  prefersReducedMotion?: () => boolean;
  onFeatureSelected?: (feature: { label: string; detail: string }) => void;
}

interface CameraDestination {
  position: THREE.Vector3;
  target: THREE.Vector3;
  direction: THREE.Vector3;
  minDistance: number;
  maxDistance: number;
}

interface CameraTween {
  fromPos: THREE.Vector3;
  toPos: THREE.Vector3;
  fromTarget: THREE.Vector3;
  toTarget: THREE.Vector3;
  startMs: number;
  durationMs: number;
}

interface LocalPoint {
  u: number;
  v: number;
}

interface ProfileSample {
  u: number;
  height: number;
}

interface PlaneFrame {
  plane: SolvedPlane;
  localRing: LocalPoint[];
  inward: { x: number; y: number };
  uWorld: THREE.Vector3;
  slopeWorld: THREE.Vector3;
  surfaceNormal: THREE.Vector3;
  minU: number;
  maxU: number;
}

export class RoofVizScene {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(38, 1, 0.1, 300);
  private controls!: OrbitControls;
  private modelGroup = new THREE.Group();
  private detailGroup = new THREE.Group();
  private sheetDetailGroup = new THREE.Group();
  private junctionDetailGroup = new THREE.Group();
  private rainwaterDetailGroup = new THREE.Group();
  private objectDetailGroup = new THREE.Group();
  private roofMeshes: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>[] = [];
  private roofBackingMeshes: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>[] = [];
  private planeFrames = new Map<string, PlaneFrame>();
  private resizeObserver!: ResizeObserver;
  private revealFrames = new Set<number>();
  private revealTimer = 0;
  private destroyed = false;
  private animationActive = false;
  private cameraWasAdjusted = false;
  private cameraTween: CameraTween | null = null;
  private environmentTexture?: THREE.Texture;
  private draftingGroundTexture?: THREE.CanvasTexture;
  private profileNormalTexture?: THREE.CanvasTexture;
  private profileRoughnessTexture?: THREE.CanvasTexture;
  private profileLod: ProfileLod = "physical";
  private cameraDirection = new THREE.Vector3(1, 0.72, 1).normalize();
  private modelBounds = new THREE.Box3();
  private modelTarget = new THREE.Vector3();
  private center: { x: number; y: number };
  private modelRadius: number;
  private profile: Profile;
  private colour: Colour;
  private gutter: GutterSpec;
  private fascia: FasciaSpec;
  private focus: RoofInsightId = "roof";
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private readonly featureClick = (event: MouseEvent): void => this.selectFeature(event);

  constructor(private options: SceneOptions) {
    this.profile = options.profile;
    this.colour = options.colour;
    this.gutter = options.gutter;
    this.fascia = options.fascia;
    const bounds = this.planBounds();
    this.center = { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
    this.modelRadius = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 8);

    let rendererRegistered = false;
    try {
      this.renderer = new THREE.WebGLRenderer({ canvas: options.canvas, antialias: true, alpha: false });
      roofVizDiagnostics.renderer(1);
      rendererRegistered = true;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 0.9;
      this.renderer.setClearColor(STAGE_BACKGROUND, 1);
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      this.environmentTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      this.scene.environment = this.environmentTexture;
      pmrem.dispose();
      this.controls = new OrbitControls(this.camera, options.canvas);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.075;
      this.controls.autoRotate = Boolean(options.autoRotate && !options.prefersReducedMotion?.());
      this.controls.autoRotateSpeed = options.autoRotateSpeed ?? 0.55;
      this.controls.enablePan = false;
      this.controls.minPolarAngle = 0.3;
      this.controls.maxPolarAngle = Math.PI / 2 - 0.07;
      this.controls.addEventListener("start", () => {
        this.cameraWasAdjusted = true;
        this.cameraTween = null;
      });
      this.controls.update();
      options.canvas.addEventListener("click", this.featureClick);

      options.solved.planes.forEach((plane) => this.planeFrames.set(plane.id, this.createPlaneFrame(plane)));
      this.buildScene();
      this.refreshModelBounds();
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(options.container);
      this.resize();
      this.cameraWasAdjusted = false;
      this.applyFit(this.computeFit(new THREE.Vector3(1, 0.72, 1)), "snap");
      this.startLoop();
    } catch (error) {
      this.controls?.dispose();
      options.canvas.removeEventListener("click", this.featureClick);
      this.resizeObserver?.disconnect();
      if (rendererRegistered) {
        this.disposeGraphics();
        roofVizDiagnostics.renderer(-1);
      }
      this.destroyed = true;
      throw error;
    }
  }

  private planBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
    const xs = this.options.packet.planes.flatMap((plane) => plane.ring.map((point) => point[0]));
    const ys = this.options.packet.planes.flatMap((plane) => plane.ring.map((point) => point[1]));
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  }

  private toWorld(x: number, y: number, z: number): THREE.Vector3 {
    return new THREE.Vector3(x - this.center.x, z, -(y - this.center.y));
  }

  private createPlaneFrame(plane: SolvedPlane): PlaneFrame {
    const centroid = plane.ring.reduce((sum, point) => ({ x: sum.x + point[0], y: sum.y + point[1] }), { x: 0, y: 0 });
    centroid.x /= plane.ring.length;
    centroid.y /= plane.ring.length;
    const centroidNormal = (centroid.x - plane.eaveA[0]) * plane.nrm.x + (centroid.y - plane.eaveA[1]) * plane.nrm.y;
    const sign = centroidNormal >= 0 ? 1 : -1;
    const inward = { x: plane.nrm.x * sign, y: plane.nrm.y * sign };
    const localRing = plane.ring.map((point) => {
      const dx = point[0] - plane.eaveA[0];
      const dy = point[1] - plane.eaveA[1];
      return { u: dx * plane.eu.x + dy * plane.eu.y, v: dx * inward.x + dy * inward.y };
    });
    const uWorld = new THREE.Vector3(plane.eu.x, 0, -plane.eu.y).normalize();
    const slopeWorld = new THREE.Vector3(inward.x, Math.tan(plane.pitchDeg * D2R), -inward.y).normalize();
    const surfaceNormal = new THREE.Vector3().crossVectors(slopeWorld, uWorld).normalize();
    if (surfaceNormal.y < 0) surfaceNormal.multiplyScalar(-1);
    return {
      plane,
      localRing,
      inward,
      uWorld,
      slopeWorld,
      surfaceNormal,
      minU: Math.min(...localRing.map((point) => point.u)),
      maxU: Math.max(...localRing.map((point) => point.u)),
    };
  }

  private pointOnPlane(frame: PlaneFrame, u: number, v: number, lift = 0): THREE.Vector3 {
    const x = frame.plane.eaveA[0] + frame.plane.eu.x * u + frame.inward.x * v;
    const y = frame.plane.eaveA[1] + frame.plane.eu.y * u + frame.inward.y * v;
    const z = frame.plane.level.eaveHeightM + v * Math.tan(frame.plane.pitchDeg * D2R);
    return this.toWorld(x, y, z).addScaledVector(frame.surfaceNormal, lift);
  }

  private selectFeature(event: MouseEvent): void {
    if (!this.options.onFeatureSelected || this.destroyed) return;
    const rect = this.options.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects([
      this.objectDetailGroup,
      this.junctionDetailGroup,
      this.rainwaterDetailGroup,
      this.sheetDetailGroup,
    ], true);
    for (const hit of hits) {
      let candidate: THREE.Object3D | null = hit.object;
      while (candidate && !candidate.userData.featureLabel) candidate = candidate.parent;
      const feature = candidate?.userData as { featureLabel?: string; featureDetail?: string } | undefined;
      if (!feature?.featureLabel) continue;
      this.options.onFeatureSelected({ label: feature.featureLabel, detail: feature.featureDetail ?? "Installed roof detail" });
      break;
    }
  }

  private tagFeature<T extends THREE.Object3D>(object: T, label: string, detail: string): T {
    object.userData.featureLabel = label;
    object.userData.featureDetail = detail;
    return object;
  }

  private intervalsAtU(frame: PlaneFrame, u: number): Array<[number, number]> {
    const intersections: number[] = [];
    frame.localRing.forEach((point, index) => {
      const next = frame.localRing[(index + 1) % frame.localRing.length];
      if (!((point.u <= u && next.u > u) || (next.u <= u && point.u > u))) return;
      const ratio = (u - point.u) / (next.u - point.u);
      intersections.push(point.v + (next.v - point.v) * ratio);
    });
    intersections.sort((a, b) => a - b);
    const intervals: Array<[number, number]> = [];
    for (let index = 0; index + 1 < intersections.length; index += 2) {
      if (intersections[index + 1] - intersections[index] > EPSILON) {
        intervals.push([intersections[index], intersections[index + 1]]);
      }
    }
    return intervals;
  }

  private createDraftingGroundTexture(): THREE.CanvasTexture {
    const size = 512;
    const fineStep = 32;
    const majorStep = fineStep * 4;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare the drafting ground texture");

    context.fillStyle = "#f7f8f4";
    context.fillRect(0, 0, size, size);
    for (let position = 0; position <= size; position += fineStep) {
      const isMajor = position % majorStep === 0;
      context.beginPath();
      context.strokeStyle = isMajor ? "rgba(70, 101, 87, 0.16)" : "rgba(70, 101, 87, 0.075)";
      context.lineWidth = isMajor ? 1.15 : 0.7;
      context.moveTo(position + 0.5, 0);
      context.lineTo(position + 0.5, size);
      context.moveTo(0, position + 0.5);
      context.lineTo(size, position + 0.5);
      context.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 4);
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  private buildScene(): void {
    this.scene.fog = new THREE.Fog(STAGE_BACKGROUND, this.modelRadius * 2.7, this.modelRadius * 5.2);

    const hemisphere = new THREE.HemisphereLight(0xf7fbff, 0xaeb6ac, 0.34);
    this.scene.add(hemisphere);
    const key = new THREE.DirectionalLight(0xfff6e7, 0.72);
    key.position.set(-this.modelRadius, this.modelRadius * 1.9, -this.modelRadius * 0.8);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    const shadowRange = this.modelRadius * 1.25;
    key.shadow.camera.left = -shadowRange;
    key.shadow.camera.right = shadowRange;
    key.shadow.camera.top = shadowRange;
    key.shadow.camera.bottom = -shadowRange;
    key.shadow.camera.near = 0.1;
    key.shadow.camera.far = this.modelRadius * 5;
    key.shadow.bias = -0.00045;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xd3e4df, 0.17);
    fill.position.set(this.modelRadius, this.modelRadius, this.modelRadius);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0xf7fbff, 0.1);
    rim.position.set(0, this.modelRadius * 0.7, -this.modelRadius * 1.8);
    this.scene.add(rim);

    this.draftingGroundTexture = this.createDraftingGroundTexture();
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map: this.draftingGroundTexture,
      roughness: 1,
      metalness: 0,
      envMapIntensity: 0.08,
    });
    const ground = new THREE.Mesh(new THREE.CircleGeometry(this.modelRadius * 2.35, 96), groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.buildWalls().forEach((wall) => this.modelGroup.add(wall));
    this.options.solved.planes.forEach((plane) => {
      const backing = this.buildFlatFace(plane);
      this.roofBackingMeshes.push(backing);
      this.modelGroup.add(backing);
      const mesh = new THREE.Mesh(this.buildProfileFaceGeometry(plane, this.profile), new THREE.MeshStandardMaterial());
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.plane = plane;
      this.roofMeshes.push(mesh);
      this.modelGroup.add(mesh);
    });
    this.modelGroup.add(this.detailGroup);
    this.modelGroup.rotation.y = -(this.options.northOffsetDeg ?? 0) * D2R;
    this.scene.add(this.modelGroup);
    this.applyAppearance(this.profile, this.colour);
  }

  private buildFlatFaceGeometry(plane: SolvedPlane): THREE.BufferGeometry {
    const triangles = THREE.ShapeUtils.triangulateShape(
      plane.ring.map((point) => new THREE.Vector2(point[0], point[1])),
      [],
    );
    const positions = plane.verts.flatMap((vertex) => {
      const world = this.toWorld(vertex.x, vertex.y, vertex.z);
      return [world.x, world.y, world.z];
    });
    const frame = this.planeFrames.get(plane.id)!;
    const uvs = frame.localRing.flatMap((point) => [point.u, point.v]);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(triangles.flat());
    geometry.computeVertexNormals();
    return geometry;
  }

  private buildFlatFace(plane: SolvedPlane): THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial> {
    const mesh = new THREE.Mesh(this.buildFlatFaceGeometry(plane), new THREE.MeshStandardMaterial());
    mesh.receiveShadow = true;
    mesh.userData.plane = plane;
    return mesh;
  }

  private clippedProfileSamples(frame: PlaneFrame, centreU: number, section: ReturnType<typeof profileCrossSection>): ProfileSample[] {
    const samples: ProfileSample[] = [];
    section.forEach((point, index) => {
      const current: ProfileSample = { u: centreU + point.offsetM, height: point.heightM };
      if (index === 0) {
        samples.push(current);
        return;
      }
      const previousPoint = section[index - 1];
      const previousU = centreU + previousPoint.offsetM;
      const splitPositions = frame.localRing
        .map((vertex) => vertex.u)
        .filter((u) => u > previousU + EPSILON && u < current.u - EPSILON)
        .sort((a, b) => a - b);
      splitPositions.forEach((u) => {
        const ratio = (u - previousU) / (current.u - previousU);
        samples.push({ u, height: previousPoint.heightM + (point.heightM - previousPoint.heightM) * ratio });
      });
      samples.push(current);
    });
    return samples.filter((sample) => sample.u >= frame.minU - EPSILON && sample.u <= frame.maxU + EPSILON);
  }

  private geometryIntervalsAtU(frame: PlaneFrame, u: number): Array<[number, number]> {
    const width = frame.maxU - frame.minU;
    if (width <= EPSILON) return [];
    const inset = Math.min(0.00001, width * 0.0001);
    return this.intervalsAtU(frame, THREE.MathUtils.clamp(u, frame.minU + inset, frame.maxU - inset));
  }

  private buildProfileFaceGeometry(plane: SolvedPlane, profile: Profile): THREE.BufferGeometry {
    const frame = this.planeFrames.get(plane.id)!;
    const spec = metalFinishForProfile(profile.id);
    const section = profileCrossSection(spec);
    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    alignedModuleCentres(frame.minU, frame.maxU, spec.moduleWidthM).forEach((centreU) => {
      const samples = this.clippedProfileSamples(frame, centreU, section);
      for (let sampleIndex = 0; sampleIndex + 1 < samples.length; sampleIndex += 1) {
        const current = samples[sampleIndex];
        const next = samples[sampleIndex + 1];
        if (next.u - current.u <= EPSILON) continue;
        const currentIntervals = this.geometryIntervalsAtU(frame, current.u);
        const nextIntervals = this.geometryIntervalsAtU(frame, next.u);
        const pairCount = Math.min(currentIntervals.length, nextIntervals.length);
        for (let intervalIndex = 0; intervalIndex < pairCount; intervalIndex += 1) {
          const [currentStartV, currentEndV] = currentIntervals[intervalIndex];
          const [nextStartV, nextEndV] = nextIntervals[intervalIndex];
          const base = positions.length / 3;
          [
            [current.u, currentStartV, current.height],
            [current.u, currentEndV, current.height],
            [next.u, nextStartV, next.height],
            [next.u, nextEndV, next.height],
          ].forEach(([u, v, height]) => {
            const world = this.pointOnPlane(frame, u, v, height + 0.0015);
            positions.push(world.x, world.y, world.z);
            uvs.push(u / 0.5, v / 0.5);
          });
          indices.push(base, base + 1, base + 3, base, base + 3, base + 2);
        }
      }
    });

    if (positions.length === 0) return this.buildFlatFaceGeometry(plane);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    return geometry;
  }

  private createRoofMaterial(profile: Profile, colour: Colour): THREE.MeshPhysicalMaterial {
    const spec = metalFinishForProfile(profile.id);
    const finishColour = new THREE.Color(colour.hex);
    const luminance = finishColour.r * 0.2126 + finishColour.g * 0.7152 + finishColour.b * 0.0722;
    finishColour.multiplyScalar(0.52 + luminance * 0.24);
    return new THREE.MeshPhysicalMaterial({
      color: finishColour,
      metalness: spec.metalness,
      roughness: spec.roughness,
      clearcoat: 0.12,
      clearcoatRoughness: 0.72,
      specularIntensity: 0.55,
      envMapIntensity: 0.4,
      side: THREE.DoubleSide,
    });
  }

  private disposeProfileTextures(): void {
    this.profileNormalTexture?.dispose();
    this.profileRoughnessTexture?.dispose();
    this.profileNormalTexture = undefined;
    this.profileRoughnessTexture = undefined;
  }

  private profileHeightAt(spec: MetalFinishSpec, ratio: number): number {
    const section = profileCrossSection(spec);
    const normalized = ((ratio % 1) + 1) % 1;
    const offset = -spec.moduleWidthM / 2 + normalized * spec.moduleWidthM;
    for (let index = 0; index + 1 < section.length; index += 1) {
      const a = section[index];
      const b = section[index + 1];
      if (offset < a.offsetM - EPSILON || offset > b.offsetM + EPSILON) continue;
      const span = b.offsetM - a.offsetM;
      const t = Math.abs(span) <= EPSILON ? 0 : (offset - a.offsetM) / span;
      return a.heightM + (b.heightM - a.heightM) * t;
    }
    return 0;
  }

  private createProfileMap(spec: MetalFinishSpec, kind: "normal" | "roughness"): THREE.CanvasTexture {
    const width = 256;
    const height = 8;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare the installed profile texture");
    const image = context.createImageData(width, height);
    for (let x = 0; x < width; x += 1) {
      const ratio = x / width;
      const delta = 1 / width;
      const before = this.profileHeightAt(spec, ratio - delta);
      const after = this.profileHeightAt(spec, ratio + delta);
      const slope = (after - before) / (spec.moduleWidthM * delta * 2);
      const nx = THREE.MathUtils.clamp(-slope, -1, 1);
      const shade = THREE.MathUtils.clamp(0.82 + (this.profileHeightAt(spec, ratio) / Math.max(spec.ribHeightM, EPSILON)) * 0.16, 0, 1);
      for (let y = 0; y < height; y += 1) {
        const offset = (y * width + x) * 4;
        if (kind === "normal") {
          image.data[offset] = Math.round(128 + nx * 108);
          image.data[offset + 1] = 128;
          image.data[offset + 2] = 245;
        } else {
          const rough = Math.round(255 * THREE.MathUtils.clamp(spec.roughness * shade, 0.2, 0.95));
          image.data[offset] = rough;
          image.data[offset + 1] = rough;
          image.data[offset + 2] = rough;
        }
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1 / spec.moduleWidthM, 1);
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  private createShadedRoofMaterial(profile: Profile, colour: Colour): THREE.MeshPhysicalMaterial {
    const spec = metalFinishForProfile(profile.id);
    this.disposeProfileTextures();
    this.profileNormalTexture = this.createProfileMap(spec, "normal");
    this.profileRoughnessTexture = this.createProfileMap(spec, "roughness");
    const material = this.createRoofMaterial(profile, colour);
    material.normalMap = this.profileNormalTexture;
    material.normalScale.set(0.18, 0.18);
    material.roughnessMap = this.profileRoughnessTexture;
    material.needsUpdate = true;
    return material;
  }

  private updateProfileLod(): void {
    const spec = metalFinishForProfile(this.profile.id);
    const viewportHeight = Math.max(1, this.options.container.clientHeight);
    const distance = Math.max(EPSILON, this.camera.position.distanceTo(this.modelTarget));
    const next = profileLodFromProjectedPixels(projectedModulePixels(spec.moduleWidthM, distance, viewportHeight, this.camera.fov));
    const visibilityMatches = next === "physical"
      ? this.roofMeshes.every((mesh) => mesh.visible) && this.roofBackingMeshes.every((mesh) => !mesh.visible)
      : this.roofMeshes.every((mesh) => !mesh.visible) && this.roofBackingMeshes.every((mesh) => mesh.visible);
    if (next === this.profileLod && visibilityMatches) return;
    this.profileLod = next;
    this.roofMeshes.forEach((mesh) => { mesh.visible = next === "physical"; });
    this.roofBackingMeshes.forEach((mesh) => { mesh.visible = next === "shaded"; });
  }

  private buildWalls(): THREE.Mesh[] {
    const walls: THREE.Mesh[] = [];
    const material = new THREE.MeshStandardMaterial({ color: 0xf1efe8, roughness: 0.94, metalness: 0, envMapIntensity: 0.12 });
    (this.options.footprints ?? []).forEach((footprint) => {
      footprint.pts.forEach((point, index) => {
        const next = footprint.pts[(index + 1) % footprint.pts.length];
        const length = Math.hypot(next[0] - point[0], next[1] - point[1]);
        const wall = new THREE.Mesh(new THREE.BoxGeometry(length, footprint.h, 0.16), material);
        wall.position.copy(this.toWorld((point[0] + next[0]) / 2, (point[1] + next[1]) / 2, footprint.h / 2));
        wall.rotation.y = Math.atan2(-(next[1] - point[1]), next[0] - point[0]);
        wall.castShadow = true;
        wall.receiveShadow = true;
        walls.push(wall);
      });
    });
    return walls;
  }

  private planeHeightAt(plane: SolvedPlane, point: XY): number {
    const distance = Math.abs((point[0] - plane.eaveA[0]) * plane.nrm.x + (point[1] - plane.eaveA[1]) * plane.nrm.y);
    return plane.level.eaveHeightM + distance * Math.tan(plane.pitchDeg * D2R);
  }

  private solvedSegment(segment: FinishTopologySegment): SolvedLine | null {
    const same = (a: XY, b: XY): boolean => Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.003;
    const candidates = segment.planeIds
      .map((id) => this.options.solved.planes.find((candidate) => candidate.id === id))
      .filter(Boolean) as SolvedPlane[];
    const plane = candidates.find((candidate) => candidate.ring.some((point, index) => {
      const next = candidate.ring[(index + 1) % candidate.ring.length];
      return (same(point, segment.start) && same(next, segment.end)) || (same(point, segment.end) && same(next, segment.start));
    })) ?? candidates.find((candidate) => candidate.ring.some((point) => same(point, segment.start) || same(point, segment.end)));
    if (!plane) return null;
    const pts3 = [segment.start, segment.end].map((point) => ({
      x: point[0],
      y: point[1],
      z: this.options.solved.weld.get(`${point[0].toFixed(4)},${point[1].toFixed(4)}`) ?? this.planeHeightAt(plane, point),
    }));
    return {
      class: segment.class,
      polyline: [segment.start, segment.end],
      planeIds: [plane.id],
      pts3,
      lengthM: Math.hypot(pts3[1].x - pts3[0].x, pts3[1].y - pts3[0].y, pts3[1].z - pts3[0].z),
    };
  }

  private sideTowardsPlane(line: SolvedLine, plane: SolvedPlane): { x: number; y: number } {
    const start = line.polyline[0];
    const end = line.polyline[line.polyline.length - 1];
    const axisLength = Math.hypot(end[0] - start[0], end[1] - start[1]) || 1;
    const axis = { x: (end[0] - start[0]) / axisLength, y: (end[1] - start[1]) / axisLength };
    const midpoint = { x: (start[0] + end[0]) / 2, y: (start[1] + end[1]) / 2 };
    const centroid = plane.ring.reduce((sum, point) => ({ x: sum.x + point[0], y: sum.y + point[1] }), { x: 0, y: 0 });
    centroid.x /= plane.ring.length;
    centroid.y /= plane.ring.length;
    const toCentroid = { x: centroid.x - midpoint.x, y: centroid.y - midpoint.y };
    const projection = toCentroid.x * axis.x + toCentroid.y * axis.y;
    let side = { x: toCentroid.x - axis.x * projection, y: toCentroid.y - axis.y * projection };
    const length = Math.hypot(side.x, side.y);
    if (length < EPSILON) side = { x: -axis.y, y: axis.x };
    else side = { x: side.x / length, y: side.y / length };
    return side;
  }

  private capGeometry(line: SolvedLine, spec: MetalFinishSpec): THREE.BufferGeometry | null {
    const planes = line.planeIds.map((id) => this.options.solved.planes.find((plane) => plane.id === id)).filter(Boolean) as SolvedPlane[];
    if (planes.length < 2 || line.pts3.length < 2) return null;
    const start = line.pts3[0];
    const end = line.pts3[line.pts3.length - 1];
    const sides = planes.slice(0, 2).map((plane) => this.sideTowardsPlane(line, plane));
    const sidePoint = (point: { x: number; y: number }, plane: SolvedPlane, side: { x: number; y: number }): THREE.Vector3 => {
      const xy: XY = [point.x + side.x * spec.ridgeCoverM, point.y + side.y * spec.ridgeCoverM];
      return this.toWorld(xy[0], xy[1], this.planeHeightAt(plane, xy) + 0.012);
    };
    const lift = spec.ribHeightM + 0.022;
    const vertices = [
      sidePoint(start, planes[0], sides[0]),
      this.toWorld(start.x, start.y, start.z + lift),
      sidePoint(start, planes[1], sides[1]),
      sidePoint(end, planes[0], sides[0]),
      this.toWorld(end.x, end.y, end.z + lift),
      sidePoint(end, planes[1], sides[1]),
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
    geometry.setIndex([
      0, 3, 4, 0, 4, 1,
      1, 4, 5, 1, 5, 2,
      0, 1, 2,
      3, 5, 4,
    ]);
    geometry.computeVertexNormals();
    return geometry;
  }

  private orientedBoxBetween(start: THREE.Vector3, end: THREE.Vector3, height: number, depth: number, material: THREE.Material): THREE.Mesh {
    const axis = end.clone().sub(start);
    const length = axis.length();
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(length, height, depth), material);
    mesh.position.copy(start).add(end).multiplyScalar(0.5);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), axis.normalize());
    mesh.castShadow = true;
    return mesh;
  }

  private verticalPrismBetween(
    start: THREE.Vector3,
    end: THREE.Vector3,
    outward: THREE.Vector3,
    height: number,
    depth: number,
    material: THREE.Material,
  ): THREE.Mesh {
    const normal = outward.clone().setY(0).normalize().multiplyScalar(depth / 2);
    const down = new THREE.Vector3(0, -height, 0);
    const vertices = [
      start.clone().add(normal), end.clone().add(normal), start.clone().add(normal).add(down), end.clone().add(normal).add(down),
      start.clone().sub(normal), end.clone().sub(normal), start.clone().sub(normal).add(down), end.clone().sub(normal).add(down),
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
    geometry.setIndex([
      0, 2, 3, 0, 3, 1,
      5, 7, 6, 5, 6, 4,
      0, 4, 6, 0, 6, 2,
      1, 3, 7, 1, 7, 5,
      0, 1, 5, 0, 5, 4,
      2, 6, 7, 2, 7, 3,
    ]);
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  private bandBetween(
    start: THREE.Vector3,
    end: THREE.Vector3,
    outward: THREE.Vector3,
    inwardWidth: number,
    outwardWidth: number,
    material: THREE.Material,
  ): THREE.Mesh {
    const vertices = [
      start.clone().addScaledVector(outward, -inwardWidth),
      end.clone().addScaledVector(outward, -inwardWidth),
      start.clone().addScaledVector(outward, outwardWidth),
      end.clone().addScaledVector(outward, outwardWidth),
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
    geometry.setIndex([0, 1, 3, 0, 3, 2]);
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  private buildFoldedCap(line: SolvedLine, spec: MetalFinishSpec, material: THREE.MeshPhysicalMaterial): THREE.Object3D[] {
    const geometry = this.capGeometry(line, spec);
    if (!geometry) return [];
    const objects: THREE.Object3D[] = [];
    const cap = new THREE.Mesh(geometry, material);
    cap.castShadow = true;
    cap.receiveShadow = true;
    objects.push(this.tagFeature(cap, line.class === "ridge" ? "Ridge capping" : "Hip capping", "Folded capping closes the meeting roof planes; the exact project detail is confirmed for the selected profile."));
    const startRaw = line.pts3[0];
    const endRaw = line.pts3[line.pts3.length - 1];
    const lift = spec.ribHeightM + 0.025;
    const start = this.toWorld(startRaw.x, startRaw.y, startRaw.z + lift);
    const end = this.toWorld(endRaw.x, endRaw.y, endRaw.z + lift);
    if (spec.ridgeClosure === "soft_round") {
      const path = new THREE.LineCurve3(start, end);
      const roll = new THREE.Mesh(new THREE.TubeGeometry(path, Math.max(2, Math.ceil(path.getLength() * 1.5)), 0.022, 8, false), material);
      roll.castShadow = true;
      objects.push(roll);
    } else {
      objects.push(this.orientedBoxBetween(start, end, 0.028, spec.ridgeClosure === "standing_seam" ? 0.09 : 0.075, material));
    }
    return objects;
  }

  private buildValley(line: SolvedLine, spec: MetalFinishSpec, material: THREE.MeshPhysicalMaterial): THREE.Object3D[] {
    const planes = line.planeIds.map((id) => this.options.solved.planes.find((plane) => plane.id === id)).filter(Boolean) as SolvedPlane[];
    if (planes.length < 2 || line.pts3.length < 2) return [];
    const start = line.pts3[0];
    const end = line.pts3[line.pts3.length - 1];
    const half = spec.valleyWidthM / 2;
    const sides = planes.slice(0, 2).map((plane) => this.sideTowardsPlane(line, plane));
    const edge = (point: { x: number; y: number }, plane: SolvedPlane, side: { x: number; y: number }): THREE.Vector3 => {
      const xy: XY = [point.x + side.x * half, point.y + side.y * half];
      return this.toWorld(xy[0], xy[1], this.planeHeightAt(plane, xy) + 0.022);
    };
    const vertices = [
      edge(start, planes[0], sides[0]),
      this.toWorld(start.x, start.y, start.z + 0.01),
      edge(start, planes[1], sides[1]),
      edge(end, planes[0], sides[0]),
      this.toWorld(end.x, end.y, end.z + 0.01),
      edge(end, planes[1], sides[1]),
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
    geometry.setIndex([0, 3, 4, 0, 4, 1, 1, 4, 5, 1, 5, 2]);
    geometry.computeVertexNormals();
    const valley = new THREE.Mesh(geometry, material);
    valley.receiveShadow = true;
    return [this.tagFeature(valley, "Folded valley gutter", "This folded channel receives water where roof planes meet and discharges at the eave. Final clearances are project-specific.")];
  }

  private gutterStopEnd(points: THREE.Vector3[], material: THREE.Material, reverse: boolean): THREE.Mesh {
    const centroid = points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).multiplyScalar(1 / points.length);
    const vertices = [centroid, ...points];
    const indices: number[] = [];
    for (let index = 1; index <= points.length; index += 1) {
      const next = index === points.length ? 1 : index + 1;
      if (reverse) indices.push(0, next, index);
      else indices.push(0, index, next);
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    return mesh;
  }

  private buildGutter(
    start: THREE.Vector3,
    end: THREE.Vector3,
    outward: THREE.Vector3,
    material: THREE.Material,
    stopStart: boolean,
    stopEnd: boolean,
  ): THREE.Object3D[] {
    const section = gutterCrossSection(this.gutter);
    const originStart = start.clone().addScaledVector(outward, 0.018).add(new THREE.Vector3(0, -0.018, 0));
    const originEnd = end.clone().addScaledVector(outward, 0.018).add(new THREE.Vector3(0, -0.018, 0));
    const positions: number[] = [];
    const indices: number[] = [];
    const startPoints: THREE.Vector3[] = [];
    const endPoints: THREE.Vector3[] = [];
    section.forEach((crossSectionPoint) => {
      [originStart, originEnd].forEach((origin, originIndex) => {
        const point = origin
          .clone()
          .addScaledVector(outward, crossSectionPoint.outwardM)
          .add(new THREE.Vector3(0, crossSectionPoint.verticalM, 0));
        positions.push(point.x, point.y, point.z);
        (originIndex === 0 ? startPoints : endPoints).push(point);
      });
    });
    for (let index = 0; index + 1 < section.length; index += 1) {
      const a = index * 2;
      indices.push(a, a + 1, a + 3, a, a + 3, a + 2);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const gutterMesh = new THREE.Mesh(geometry, material);
    gutterMesh.castShadow = true;
    gutterMesh.receiveShadow = true;
    const objects: THREE.Object3D[] = [this.tagFeature(
      gutterMesh,
      this.gutter.name,
      "A colour-matched visual option. Final capacity, fall, outlets and downpipes are designed for the job.",
    )];
    if (stopStart) objects.push(this.gutterStopEnd(startPoints, material, true));
    if (stopEnd) objects.push(this.gutterStopEnd(endPoints, material, false));
    if (this.gutter.bracket === "external" || this.gutter.bracket === "heavy_external") {
      const length = start.distanceTo(end);
      const count = Math.max(1, Math.min(12, Math.floor(length / (this.gutter.bracket === "heavy_external" ? 0.6 : 0.75))));
      for (let index = 0; index <= count; index += 1) {
        const at = start.clone().lerp(end, index / count).addScaledVector(outward, 0.025).add(new THREE.Vector3(0, -0.035, 0));
        const bracketPath = new THREE.CatmullRomCurve3([
          at,
          at.clone().addScaledVector(outward, this.gutter.widthM * 0.45).add(new THREE.Vector3(0, -this.gutter.depthM * 0.72, 0)),
          at.clone().addScaledVector(outward, this.gutter.widthM * 0.92).add(new THREE.Vector3(0, -0.004, 0)),
        ]);
        const bracket = new THREE.Mesh(new THREE.TubeGeometry(bracketPath, 8, this.gutter.bracket === "heavy_external" ? 0.007 : 0.004, 6, false), material);
        bracket.castShadow = true;
        objects.push(bracket);
      }
    }
    return objects;
  }

  private buildEaveAssembly(
    line: SolvedLine,
    finishMaterial: THREE.MeshPhysicalMaterial,
    stopStart = true,
    stopEnd = true,
  ): THREE.Object3D[] {
    const plane = this.options.solved.planes.find((candidate) => candidate.id === line.planeIds[0]);
    if (!plane || line.pts3.length < 2) return [];
    const first = line.pts3[0];
    const last = line.pts3[line.pts3.length - 1];
    const start = this.toWorld(first.x, first.y, first.z);
    const end = this.toWorld(last.x, last.y, last.z);
    const inward = this.sideTowardsPlane(line, plane);
    const outward = new THREE.Vector3(-inward.x, 0, inward.y).normalize();
    const fasciaMaterial = finishMaterial.clone();
    fasciaMaterial.color.lerp(new THREE.Color(0xf4f3ec), 0.18);
    fasciaMaterial.roughness = Math.min(0.76, fasciaMaterial.roughness + 0.12);
    const fasciaTopOffset = outward.clone().multiplyScalar(this.fascia.attachment === "internal" ? -0.002 : 0.016).add(new THREE.Vector3(0, -0.024, 0));
    const fascia = this.verticalPrismBetween(
      start.clone().add(fasciaTopOffset),
      end.clone().add(fasciaTopOffset),
      outward,
      this.fascia.heightM,
      this.fascia.faceDepthM,
      fasciaMaterial,
    );
    const gutter = this.buildGutter(start, end, outward, finishMaterial, stopStart, stopEnd);
    const dripTop = this.bandBetween(
      start.clone().add(new THREE.Vector3(0, 0.012, 0)),
      end.clone().add(new THREE.Vector3(0, 0.012, 0)),
      outward,
      0.026,
      0.045,
      finishMaterial,
    );
    const dripFace = this.verticalPrismBetween(
      start.clone().addScaledVector(outward, 0.043).add(new THREE.Vector3(0, 0.012, 0)),
      end.clone().addScaledVector(outward, 0.043).add(new THREE.Vector3(0, 0.012, 0)),
      outward,
      0.035,
      0.008,
      finishMaterial,
    );
    return [
      this.tagFeature(fascia, this.fascia.name, "The selected fascia finishes and protects the roof edge. Final attachment is confirmed with the rainwater system."),
      ...gutter,
      this.tagFeature(dripTop, "Eave and drip edge", "Sheets finish over a folded drip edge and profile closure before water enters the gutter."),
      dripFace,
    ];
  }

  private buildRakeAssembly(line: SolvedLine, spec: MetalFinishSpec, material: THREE.MeshPhysicalMaterial): THREE.Object3D[] {
    const plane = this.options.solved.planes.find((candidate) => candidate.id === line.planeIds[0]);
    const first = line.pts3[0];
    const last = line.pts3[line.pts3.length - 1];
    if (!plane || !first || !last) return [];
    const inward = this.sideTowardsPlane(line, plane);
    const outward = new THREE.Vector3(-inward.x, 0, inward.y).normalize();
    const lift = spec.ribHeightM + 0.016;
    const start = this.toWorld(first.x, first.y, first.z + lift);
    const end = this.toWorld(last.x, last.y, last.z + lift);
    const top = this.bandBetween(start, end, outward, 0.055, 0.045, material);
    const faceStart = start.clone().addScaledVector(outward, 0.04);
    const faceEnd = end.clone().addScaledVector(outward, 0.04);
    const face = this.verticalPrismBetween(faceStart, faceEnd, outward, 0.105, 0.014, material);
    return [
      this.tagFeature(top, "Barge flashing", "A folded edge flashing keeps the sloping roof edge clean and weathered."),
      face,
    ];
  }

  private buildGutterJunction(at: XY, material: THREE.Material): THREE.Object3D {
    const adjacent = this.options.solved.planes.filter((plane) => plane.ring.some((point) => Math.hypot(point[0] - at[0], point[1] - at[1]) < 0.003));
    const level = adjacent.length
      ? Math.max(...adjacent.map((plane) => this.planeHeightAt(plane, at)))
      : Math.min(...this.options.packet.levels.map((item) => item.eaveHeightM));
    const joint = new THREE.Mesh(new THREE.SphereGeometry(1, 10, 6), material);
    joint.position.copy(this.toWorld(at[0], at[1], level - 0.055));
    joint.scale.set(this.gutter.widthM * 0.7, Math.max(0.025, this.gutter.depthM * 0.45), this.gutter.widthM * 0.7);
    joint.castShadow = true;
    return this.tagFeature(joint, "Mitred gutter corner", "Connected gutter runs turn through one continuous corner; stop ends appear only at genuine terminals.");
  }

  private buildWallFlashing(line: SolvedLine, spec: MetalFinishSpec, material: THREE.MeshPhysicalMaterial): THREE.Object3D[] {
    const descriptor = flashingDescriptor(line.class, spec);
    const plane = this.options.solved.planes.find((candidate) => candidate.id === line.planeIds[0]);
    const first = line.pts3[0];
    const last = line.pts3[line.pts3.length - 1];
    if (!plane || !first || !last) return [];
    const inward = this.sideTowardsPlane(line, plane);
    const outward = new THREE.Vector3(-inward.x, 0, inward.y).normalize();
    const start = this.toWorld(first.x, first.y, first.z + spec.ribHeightM + 0.012);
    const end = this.toWorld(last.x, last.y, last.z + spec.ribHeightM + 0.012);
    const tray = this.bandBetween(start, end, outward, Math.min(descriptor.coverM, 0.16), 0.018, material);
    const wallTopStart = start.clone().addScaledVector(outward, 0.014).add(new THREE.Vector3(0, descriptor.upstandM, 0));
    const wallTopEnd = end.clone().addScaledVector(outward, 0.014).add(new THREE.Vector3(0, descriptor.upstandM, 0));
    const wall = this.verticalPrismBetween(wallTopStart, wallTopEnd, outward, descriptor.upstandM, 0.012, material);
    const objects: THREE.Object3D[] = [this.tagFeature(tray, descriptor.homeownerLabel, "Indicative folded metalwork; the project detail is confirmed for the wall, pitch and selected profile."), wall];
    if (line.class === "step_flashing") {
      const length = start.distanceTo(end);
      const steps = Math.max(2, Math.min(10, Math.round(length / 0.45)));
      for (let index = 1; index < steps; index += 1) {
        const point = start.clone().lerp(end, index / steps).addScaledVector(outward, 0.022).add(new THREE.Vector3(0, descriptor.upstandM * 0.72, 0));
        const step = new THREE.Mesh(new THREE.BoxGeometry(0.018, descriptor.upstandM * 0.42, 0.11), material);
        step.position.copy(point);
        objects.push(step);
      }
    }
    if (line.class === "parapet") {
      const cap = this.bandBetween(
        wallTopStart.clone().add(new THREE.Vector3(0, 0.008, 0)),
        wallTopEnd.clone().add(new THREE.Vector3(0, 0.008, 0)),
        outward,
        0.045,
        0.045,
        material,
      );
      objects.push(cap);
    }
    return objects;
  }

  private objectFootprintGeometry(plane: SolvedPlane, at: XY, widthM: number, depthM: number, lift: number): THREE.BufferGeometry {
    const corners: XY[] = [
      [at[0] - widthM / 2, at[1] - depthM / 2],
      [at[0] + widthM / 2, at[1] - depthM / 2],
      [at[0] + widthM / 2, at[1] + depthM / 2],
      [at[0] - widthM / 2, at[1] + depthM / 2],
    ];
    const vertices = corners.map((point) => this.toWorld(point[0], point[1], this.planeHeightAt(plane, point) + lift));
    const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.computeVertexNormals();
    return geometry;
  }

  private buildMappedObject(object: RoofvizPacket["objects"][number], roofMaterial: THREE.MeshPhysicalMaterial): THREE.Object3D[] {
    const plane = this.options.solved.planes.find((candidate) => candidate.id === object.planeId);
    if (!plane) return [];
    const presentation = objectPresentation(object);
    const centre = this.toWorld(object.at[0], object.at[1], this.planeHeightAt(plane, object.at) + 0.055);
    if (presentation.mode === "mapped_marker") {
      const markerMaterial = new THREE.MeshStandardMaterial({ color: 0xf4ce2a, roughness: 0.62, metalness: 0.08 });
      const marker = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.022, 6, 20), markerMaterial);
      marker.position.copy(centre);
      marker.rotation.x = Math.PI / 2;
      return [this.tagFeature(marker, presentation.label, presentation.detail)];
    }

    const flashingMaterial = roofMaterial.clone();
    flashingMaterial.color.multiplyScalar(0.63);
    flashingMaterial.roughness = Math.min(0.78, flashingMaterial.roughness + 0.18);
    const zone = new THREE.Mesh(
      this.objectFootprintGeometry(plane, object.at, presentation.widthM + 0.22, presentation.depthM + 0.22, 0.034),
      flashingMaterial,
    );
    zone.receiveShadow = true;
    this.tagFeature(zone, presentation.label, presentation.detail);
    const objects: THREE.Object3D[] = [zone];

    if (object.type === "skylight") {
      const glazing = new THREE.Mesh(
        this.objectFootprintGeometry(plane, object.at, presentation.widthM, presentation.depthM, 0.095),
        new THREE.MeshPhysicalMaterial({ color: 0x6e8990, roughness: 0.2, metalness: 0.04, transmission: 0.18, transparent: true, opacity: 0.86 }),
      );
      glazing.castShadow = true;
      objects.push(this.tagFeature(glazing, presentation.label, presentation.detail));
    } else if (object.type === "solar_array") {
      const panel = new THREE.Mesh(
        this.objectFootprintGeometry(plane, object.at, presentation.widthM, presentation.depthM, 0.105),
        new THREE.MeshPhysicalMaterial({ color: 0x15252f, roughness: 0.28, metalness: 0.16, clearcoat: 0.52, clearcoatRoughness: 0.24 }),
      );
      panel.castShadow = true;
      objects.push(this.tagFeature(panel, presentation.label, presentation.detail));
      const columns = Math.max(1, Math.min(8, Math.round(presentation.widthM / 1.7)));
      const rows = Math.max(1, Math.min(5, Math.round(presentation.depthM / 1.05)));
      const gridMaterial = new THREE.LineBasicMaterial({ color: 0xa9bac0, transparent: true, opacity: 0.72 });
      for (let column = 1; column < columns; column += 1) {
        const x = object.at[0] - presentation.widthM / 2 + (presentation.widthM * column) / columns;
        const ends: XY[] = [[x, object.at[1] - presentation.depthM / 2], [x, object.at[1] + presentation.depthM / 2]];
        objects.push(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ends.map((point) => this.toWorld(point[0], point[1], this.planeHeightAt(plane, point) + 0.11))), gridMaterial));
      }
      for (let row = 1; row < rows; row += 1) {
        const y = object.at[1] - presentation.depthM / 2 + (presentation.depthM * row) / rows;
        const ends: XY[] = [[object.at[0] - presentation.widthM / 2, y], [object.at[0] + presentation.widthM / 2, y]];
        objects.push(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ends.map((point) => this.toWorld(point[0], point[1], this.planeHeightAt(plane, point) + 0.11))), gridMaterial));
      }
    } else {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(Math.max(presentation.widthM, presentation.depthM) * 0.42, 0.025, 6, 24), flashingMaterial);
      ring.position.copy(centre);
      ring.rotation.x = Math.PI / 2;
      objects.push(this.tagFeature(ring, presentation.label, presentation.detail));
    }
    return objects;
  }

  private buildSheetJoints(frame: PlaneFrame, spec: MetalFinishSpec, material: THREE.Material): THREE.Object3D[] {
    const objects: THREE.Object3D[] = [];
    alignedSheetJoints(frame.minU, frame.maxU, spec.sheetCoverM).forEach((u) => {
      this.intervalsAtU(frame, u).forEach(([startV, endV]) => {
        const lift = spec.ribHeightM + 0.004;
        const start = this.pointOnPlane(frame, u, startV, lift);
        const end = this.pointOnPlane(frame, u, endV, lift);
        const path = new THREE.LineCurve3(start, end);
        const joint = new THREE.Mesh(new THREE.TubeGeometry(path, Math.max(2, Math.ceil(path.getLength())), 0.0045, 5, false), material);
        joint.castShadow = true;
        objects.push(this.tagFeature(joint, "Sheet side lap", "Visible lap rhythm follows the published sheet cover. It does not change the mapped roof quantity."));
      });
    });
    return objects;
  }

  private buildFasteners(frame: PlaneFrame, spec: MetalFinishSpec, material: THREE.Material): THREE.Object3D[] {
    if (spec.fixingAppearance !== "exposed") return [];
    const positions: THREE.Vector3[] = [];
    const centres = alignedModuleCentres(frame.minU, frame.maxU, spec.moduleWidthM);
    const stride = Math.max(1, Math.ceil(centres.length / 48));
    centres.filter((_, index) => index % stride === 0).forEach((u) => {
      this.intervalsAtU(frame, u).forEach(([startV, endV]) => {
        const rows = Math.max(1, Math.min(12, Math.floor((endV - startV) / 1.15)));
        for (let row = 0; row <= rows; row += 1) {
          const v = startV + ((endV - startV) * row) / rows;
          positions.push(this.pointOnPlane(frame, u, v, spec.ribHeightM + 0.012));
        }
      });
    });
    if (!positions.length) return [];
    const fasteners = new THREE.InstancedMesh(new THREE.SphereGeometry(0.012, 6, 4), material, positions.length);
    const matrix = new THREE.Matrix4();
    positions.forEach((position, index) => {
      matrix.makeTranslation(position.x, position.y, position.z);
      fasteners.setMatrixAt(index, matrix);
    });
    fasteners.instanceMatrix.needsUpdate = true;
    fasteners.castShadow = true;
    return [this.tagFeature(fasteners, "Exposed fixing appearance", "Representative fixing heads distinguish this exposed-fix family; they are not a structural fixing schedule.")];
  }

  private buildCapJunction(at: XY, spec: MetalFinishSpec, material: THREE.Material): THREE.Object3D {
    const adjacent = this.options.solved.planes.filter((plane) => plane.ring.some((point) => Math.hypot(point[0] - at[0], point[1] - at[1]) < 0.003));
    const z = adjacent.length
      ? Math.max(...adjacent.map((plane) => this.planeHeightAt(plane, at)))
      : Math.max(...this.options.packet.levels.map((level) => level.eaveHeightM));
    const junction = new THREE.Mesh(
      new THREE.CylinderGeometry(1, 1, spec.ridgeClosure === "soft_round" ? 0.035 : 0.026, spec.ridgeClosure === "soft_round" ? 12 : 6),
      material,
    );
    junction.position.copy(this.toWorld(at[0], at[1], z + spec.ribHeightM + 0.02));
    junction.scale.set(spec.ridgeCoverM * 0.72, 1, spec.ridgeCoverM * 0.72);
    junction.castShadow = true;
    return this.tagFeature(junction, "Capping junction", "Connected ridge and hip cappings close together as one finished weathering junction.");
  }

  private clearDetailGroup(): void {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    this.detailGroup.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        geometries.add(object.geometry);
        (Array.isArray(object.material) ? object.material : [object.material]).forEach((material) => materials.add(material));
      }
    });
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
    this.sheetDetailGroup.clear();
    this.junctionDetailGroup.clear();
    this.rainwaterDetailGroup.clear();
    this.objectDetailGroup.clear();
    this.detailGroup.clear();
  }

  private setGroupHighlight(group: THREE.Group, highlighted: boolean): void {
    group.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => {
        if (!(material instanceof THREE.MeshStandardMaterial)) return;
        material.emissive.setHex(highlighted ? 0xf5d63b : 0x000000);
        material.emissiveIntensity = highlighted ? 0.34 : 0;
        material.needsUpdate = true;
      });
    });
  }

  private applyFocus(): void {
    this.sheetDetailGroup.visible = this.focus === "roof" || this.focus === "pitch";
    this.junctionDetailGroup.visible = this.focus === "roof" || this.focus === "junctions";
    this.rainwaterDetailGroup.visible = this.focus === "roof" || this.focus === "rainwater";
    this.objectDetailGroup.visible = true;
    this.setGroupHighlight(this.sheetDetailGroup, this.focus === "pitch");
    this.setGroupHighlight(this.junctionDetailGroup, this.focus === "junctions");
    this.setGroupHighlight(this.rainwaterDetailGroup, this.focus === "rainwater");
  }

  private buildAppearanceDetails(profile: Profile, colour: Colour, roofMaterial: THREE.MeshPhysicalMaterial): void {
    const spec = metalFinishForProfile(profile.id);
    const capMaterial = roofMaterial.clone();
    capMaterial.color.multiplyScalar(0.78);
    capMaterial.roughness = Math.min(0.72, spec.roughness + 0.08);
    capMaterial.metalness = Math.min(capMaterial.metalness, 0.05);
    capMaterial.clearcoat = 0.08;
    capMaterial.specularIntensity = 0.25;
    const valleyMaterial = roofMaterial.clone();
    valleyMaterial.color.multiplyScalar(0.88);
    valleyMaterial.roughness = Math.min(0.76, spec.roughness + 0.14);
    valleyMaterial.metalness = Math.min(valleyMaterial.metalness, 0.04);
    valleyMaterial.specularIntensity = 0.32;
    const jointMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color(colour.hex).multiplyScalar(0.34),
      roughness: 0.74,
      metalness: 0.06,
      transparent: true,
      opacity: 0.62,
    });

    this.detailGroup.add(this.sheetDetailGroup, this.junctionDetailGroup, this.rainwaterDetailGroup, this.objectDetailGroup);
    this.planeFrames.forEach((frame) => {
      this.buildSheetJoints(frame, spec, jointMaterial).forEach((object) => this.sheetDetailGroup.add(object));
      this.buildFasteners(frame, spec, jointMaterial).forEach((object) => this.sheetDetailGroup.add(object));
    });
    this.options.solved.lines.forEach((line) => {
      if (line.class === "ridge" || line.class === "hip") {
        this.buildFoldedCap(line, spec, capMaterial).forEach((object) => this.junctionDetailGroup.add(object));
      } else if (line.class === "valley") {
        this.buildValley(line, spec, valleyMaterial).forEach((object) => this.junctionDetailGroup.add(object));
      } else if (line.class === "rake") {
        this.buildRakeAssembly(line, spec, capMaterial).forEach((object) => this.junctionDetailGroup.add(object));
      } else if (line.class === "step_flashing" || line.class === "apron_flashing" || line.class === "parapet") {
        this.buildWallFlashing(line, spec, capMaterial).forEach((object) => this.junctionDetailGroup.add(object));
      }
    });

    const edgeTopology = buildFinishTopology(this.options.packet.lines, ["eave", "gutter"]);
    edgeTopology.segments.forEach((segment) => {
      const solved = this.solvedSegment(segment);
      if (!solved) return;
      this.buildEaveAssembly(solved, capMaterial, segment.startTerminal, segment.endTerminal)
        .forEach((object) => this.rainwaterDetailGroup.add(object));
    });
    edgeTopology.nodes.filter((node) => node.degree > 1).forEach((node) => {
      this.rainwaterDetailGroup.add(this.buildGutterJunction(node.at, capMaterial));
    });

    const capTopology = buildFinishTopology(this.options.packet.lines, ["ridge", "hip"]);
    capTopology.nodes.filter((node) => node.degree > 1).forEach((node) => {
      this.junctionDetailGroup.add(this.buildCapJunction(node.at, spec, capMaterial));
    });
    this.options.packet.objects.forEach((object) => {
      this.buildMappedObject(object, roofMaterial).forEach((mesh) => this.objectDetailGroup.add(mesh));
    });
    this.applyFocus();
  }

  private startLoop(): void {
    if (this.animationActive) return;
    this.animationActive = true;
    roofVizDiagnostics.animationLoop(1);
    this.renderer.setAnimationLoop(() => {
      this.advanceCameraTween();
      this.controls.update();
      this.updateProfileLod();
      this.renderer.render(this.scene, this.camera);
    });
  }

  private advanceCameraTween(): void {
    const tween = this.cameraTween;
    if (!tween) return;
    const progress = THREE.MathUtils.clamp((performance.now() - tween.startMs) / tween.durationMs, 0, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    this.camera.position.lerpVectors(tween.fromPos, tween.toPos, eased);
    this.controls.target.lerpVectors(tween.fromTarget, tween.toTarget, eased);
    if (progress >= 1) this.cameraTween = null;
  }

  /** Render a single frame synchronously — used by the standalone snapshot export. */
  renderNow(): void {
    if (this.destroyed) return;
    this.renderer.render(this.scene, this.camera);
  }

  private resize(): void {
    if (this.destroyed) return;
    const width = Math.max(1, this.options.container.clientWidth);
    const height = Math.max(1, this.options.container.clientHeight);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    if (!this.cameraWasAdjusted) this.applyFit(this.computeFit(this.cameraDirection), "snap");
    this.updateProfileLod();
  }

  private refreshModelBounds(): void {
    this.modelGroup.updateWorldMatrix(true, true);
    this.modelBounds.setFromObject(this.modelGroup);
    if (this.modelBounds.isEmpty()) {
      this.modelBounds.set(
        new THREE.Vector3(-this.modelRadius / 2, 0, -this.modelRadius / 2),
        new THREE.Vector3(this.modelRadius / 2, this.modelRadius / 2, this.modelRadius / 2),
      );
    }
    this.modelBounds.getCenter(this.modelTarget);
  }

  private computeFit(direction: THREE.Vector3): CameraDestination {
    const normalizedDirection = direction.clone().normalize();
    const distance = perspectiveFitDistance(
      this.modelBounds,
      this.modelTarget,
      normalizedDirection,
      this.camera.fov,
      this.camera.aspect,
      this.camera.aspect < 0.9 ? 1.16 : 1.1,
    );
    return {
      position: this.modelTarget.clone().add(normalizedDirection.clone().multiplyScalar(distance)),
      target: this.modelTarget.clone(),
      direction: normalizedDirection,
      minDistance: distance * 0.34,
      maxDistance: distance * 2.2,
    };
  }

  private applyFit(destination: CameraDestination, mode: "snap" | "tween"): void {
    this.cameraDirection.copy(destination.direction);
    // Snap the distance clamps at the start — only position/target animate.
    this.controls.minDistance = destination.minDistance;
    this.controls.maxDistance = destination.maxDistance;
    if (mode === "snap" || (this.options.prefersReducedMotion?.() ?? false)) {
      this.cameraTween = null;
      this.controls.target.copy(destination.target);
      this.camera.position.copy(destination.position);
      this.controls.update();
      return;
    }
    this.startCameraTween(destination.position, destination.target, 800);
  }

  private startCameraTween(toPos: THREE.Vector3, toTarget: THREE.Vector3, durationMs: number): void {
    this.cameraTween = {
      fromPos: this.camera.position.clone(),
      toPos: toPos.clone(),
      fromTarget: this.controls.target.clone(),
      toTarget: toTarget.clone(),
      startMs: performance.now(),
      durationMs,
    };
  }

  setView(view: CardinalView): void {
    const directions: Record<CardinalView, THREE.Vector3> = {
      north: new THREE.Vector3(0, 0.55, -1),
      east: new THREE.Vector3(1, 0.55, 0),
      south: new THREE.Vector3(0, 0.55, 1),
      west: new THREE.Vector3(-1, 0.55, 0),
    };
    this.cameraWasAdjusted = false;
    this.applyFit(this.computeFit(directions[view]), "tween");
  }

  zoom(factor: number): void {
    this.cameraWasAdjusted = true;
    const offset = this.camera.position.clone().sub(this.controls.target);
    const nextDistance = THREE.MathUtils.clamp(offset.length() * factor, this.controls.minDistance, this.controls.maxDistance);
    const nextPosition = this.controls.target.clone().add(offset.normalize().multiplyScalar(nextDistance));
    if (this.options.prefersReducedMotion?.() ?? false) {
      this.cameraTween = null;
      this.camera.position.copy(nextPosition);
      this.controls.update();
      return;
    }
    this.startCameraTween(nextPosition, this.controls.target.clone(), 240);
  }

  reset(): void {
    this.cameraWasAdjusted = false;
    this.applyFit(this.computeFit(new THREE.Vector3(1, 0.72, 1)), "tween");
  }

  applyAppearance(profile: Profile, colour: Colour): void {
    this.profile = profile;
    this.colour = colour;
    const detailsWereVisible = this.detailGroup.visible;
    this.clearDetailGroup();
    const roofMaterial = this.createRoofMaterial(profile, colour);
    const shadedMaterial = this.createShadedRoofMaterial(profile, colour);
    const previousMaterials = new Set<THREE.Material>();
    this.roofMeshes.forEach((mesh, index) => {
      previousMaterials.add(mesh.material);
      mesh.geometry.dispose();
      mesh.geometry = this.buildProfileFaceGeometry(this.options.solved.planes[index], profile);
      mesh.material = roofMaterial.clone();
    });
    this.roofBackingMeshes.forEach((mesh) => {
      previousMaterials.add(mesh.material);
      const backing = shadedMaterial.clone();
      backing.polygonOffset = true;
      backing.polygonOffsetFactor = 1;
      backing.polygonOffsetUnits = 1;
      mesh.material = backing;
    });
    previousMaterials.forEach((material) => material.dispose());
    this.buildAppearanceDetails(profile, colour, roofMaterial);
    roofMaterial.dispose();
    shadedMaterial.dispose();
    this.profileLod = "physical";
    this.updateProfileLod();
    this.detailGroup.visible = detailsWereVisible;
  }

  applyGutter(gutter: GutterSpec): void {
    this.gutter = gutter;
    const detailsWereVisible = this.detailGroup.visible;
    this.clearDetailGroup();
    const roofMaterial = this.createRoofMaterial(this.profile, this.colour);
    this.buildAppearanceDetails(this.profile, this.colour, roofMaterial);
    roofMaterial.dispose();
    this.detailGroup.visible = detailsWereVisible;
  }

  applyFascia(fascia: FasciaSpec): void {
    this.fascia = fascia;
    const detailsWereVisible = this.detailGroup.visible;
    this.clearDetailGroup();
    const roofMaterial = this.createRoofMaterial(this.profile, this.colour);
    this.buildAppearanceDetails(this.profile, this.colour, roofMaterial);
    roofMaterial.dispose();
    this.detailGroup.visible = detailsWereVisible;
  }

  setFocus(focus: RoofInsightId): void {
    this.focus = focus;
    this.applyFocus();
  }

  reveal(instant: boolean, onComplete?: () => void): void {
    this.cancelReveal();
    const revealMeshes = [...this.roofMeshes, ...this.roofBackingMeshes];
    if (instant) {
      this.detailGroup.visible = true;
      revealMeshes.forEach((mesh) => { mesh.position.y = 0; mesh.material.opacity = 1; mesh.material.transparent = false; });
      this.updateProfileLod();
      onComplete?.();
      return;
    }
    this.detailGroup.visible = false;
    revealMeshes.forEach((mesh, index) => {
      mesh.position.y = 1.8;
      mesh.material.transparent = true;
      mesh.material.opacity = 0;
      const start = performance.now() + index * 80;
      const step = (now: number): void => {
        if (this.destroyed) return;
        const progress = THREE.MathUtils.clamp((now - start) / 620, 0, 1);
        if (progress > 0) {
          const eased = 1 - Math.pow(1 - progress, 3);
          mesh.position.y = 1.8 * (1 - eased);
          mesh.material.opacity = eased;
        }
        if (progress < 1) {
          const frame = requestAnimationFrame(step);
          this.revealFrames.add(frame);
        } else {
          mesh.material.transparent = false;
          mesh.material.opacity = 1;
        }
      };
      const frame = requestAnimationFrame(step);
      this.revealFrames.add(frame);
    });
    this.revealTimer = window.setTimeout(() => {
      this.detailGroup.visible = true;
      onComplete?.();
    }, revealMeshes.length * 80 + 760);
  }

  private cancelReveal(): void {
    this.revealFrames.forEach((frame) => cancelAnimationFrame(frame));
    this.revealFrames.clear();
    window.clearTimeout(this.revealTimer);
    this.revealTimer = 0;
  }

  private disposeGraphics(): void {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        geometries.add(object.geometry);
        (Array.isArray(object.material) ? object.material : [object.material]).forEach((material) => materials.add(material));
      }
    });
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
    this.draftingGroundTexture?.dispose();
    this.draftingGroundTexture = undefined;
    this.environmentTexture?.dispose();
    this.environmentTexture = undefined;
    this.disposeProfileTextures();
    this.scene.environment = null;
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cancelReveal();
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.options.canvas.removeEventListener("click", this.featureClick);
    if (this.animationActive) {
      this.renderer.setAnimationLoop(null);
      this.animationActive = false;
      roofVizDiagnostics.animationLoop(-1);
    }
    this.disposeGraphics();
    roofVizDiagnostics.renderer(-1);
    this.roofMeshes = [];
    this.roofBackingMeshes = [];
    this.planeFrames.clear();
  }
}

export function footprintsFromPacket(packet: RoofvizPacket): Footprint[] {
  const eavePoints = packet.lines.filter((line) => line.class === "eave").flatMap((line) => line.polyline);
  if (eavePoints.length < 3) return [];
  const unique = [...new Map(eavePoints.map((point) => [`${point[0]},${point[1]}`, point] as const)).values()];
  const height = Math.min(...packet.levels.map((level) => level.eaveHeightM));
  return [{ pts: unique, h: height }];
}
